import { useState, type CSSProperties, type MouseEvent } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { usePreferences } from '../../contexts/PreferencesContext';
import { UPGRADE_WHATSAPP_URL, percentualUsado } from '../../utils/planoGratuito';
import { useTransactionUsage } from './useTransactionUsage';
import styles from './FreePlanChip.module.css';

/**
 * Selo do plano Gratuito (MAN-364) para o celular, ao lado do título da página.
 * No celular a barra completa ocupava metade da primeira tela; no lugar dela fica
 * só a contagem ("10/50"), em vermelho ao atingir o limite. Tocar abre um cartão
 * com a barra de progresso e o "Fazer Upgrade". No desktop o selo não aparece.
 */
export function FreePlanChip() {
    const uso = useTransactionUsage();
    const { t } = usePreferences();
    const [posicao, setPosicao] = useState<CSSProperties | null>(null);

    if (!uso?.freePlan || !uso.limit) return null;

    const atingiu = uso.reached;
    const usados = String(Math.min(uso.used, uso.limit));
    const limite = String(uso.limit);

    // O cartão é `position: fixed` porque a área do título corta o que sai dela
    // (overflow hidden); a posição vem do próprio selo.
    const abrir = (e: MouseEvent<HTMLButtonElement>) => {
        const r = e.currentTarget.getBoundingClientRect();
        const largura = Math.min(320, window.innerWidth - 32);
        const esquerda = Math.min(Math.max(16, r.left), window.innerWidth - largura - 16);
        setPosicao({ top: r.bottom + 8, left: esquerda, width: largura });
    };

    return (
        <>
            <button
                type="button"
                className={`${styles.selo} ${atingiu ? styles.seloLimite : styles.seloContagem}`}
                onClick={(e) => (posicao ? setPosicao(null) : abrir(e))}
                aria-expanded={!!posicao}
                aria-label={atingiu ? t('planoGratuito.limiteTitulo') : t('planoGratuito.contagemTitulo', { limite })}
            >
                <FiAlertTriangle size={13} />
                {usados}/{limite}
            </button>

            {posicao && (
                <>
                    <div className={styles.fundo} onClick={() => setPosicao(null)} />
                    <div className={styles.cartao} style={posicao} role="dialog">
                        <strong className={styles.titulo}>
                            {atingiu ? t('planoGratuito.limiteTitulo') : t('planoGratuito.contagemTitulo', { limite })}
                        </strong>
                        {atingiu ? (
                            <span className={styles.descricao}>{t('planoGratuito.limiteModal', { limite })}</span>
                        ) : (
                            <span className={styles.contagem}>
                                <span className={styles.trilho} aria-hidden="true">
                                    <span className={styles.progresso} style={{ width: `${percentualUsado(uso.used, uso.limit)}%` }} />
                                </span>
                                {usados}/{limite}
                            </span>
                        )}
                        <a className={styles.upgrade} href={UPGRADE_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                            {t('planoGratuito.fazerUpgrade')}
                        </a>
                    </div>
                </>
            )}
        </>
    );
}
